import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { moveStableRoot } from "../foundations/moveStableRoot";

const useBrowserLayoutEffect = typeof document === "undefined" ? useEffect : useLayoutEffect;

interface PresentationTargetRegistration {
  node: HTMLDivElement;
  active: boolean;
  registeredOrder: number;
  activatedOrder: number;
}

class VideoPresentationCoordinator {
  readonly root: HTMLDivElement;
  private readonly targets = new Map<symbol, PresentationTargetRegistration>();
  private currentTarget: symbol | null = null;
  private order = 0;

  constructor(ownerDocument: Document) {
    this.root = ownerDocument.createElement("div");
    this.root.setAttribute("data-c6-video-presentation-root", "");
    this.root.style.width = "100%";
    this.root.style.minWidth = "0";
  }

  updateTarget(token: symbol, node: HTMLDivElement, active: boolean) {
    const previous = this.targets.get(token);
    const activationChanged = active && (!previous || !previous.active);
    const next: PresentationTargetRegistration = {
      node,
      active,
      registeredOrder: previous?.registeredOrder ?? ++this.order,
      activatedOrder: activationChanged ? ++this.order : (previous?.activatedOrder ?? 0),
    };
    this.targets.set(token, next);
    this.reconcileTarget();
  }

  removeTarget(token: symbol, expectedNode: HTMLDivElement) {
    const registration = this.targets.get(token);
    if (!registration || registration.node !== expectedNode) return;

    this.targets.delete(token);
    if (this.currentTarget === token) this.currentTarget = null;
    this.reconcileTarget();
  }

  private reconcileTarget() {
    let selectedToken: symbol | null = null;
    let selected: PresentationTargetRegistration | null = null;

    for (const [token, registration] of this.targets) {
      if (
        registration.active &&
        (!selected || registration.activatedOrder > selected.activatedOrder)
      ) {
        selectedToken = token;
        selected = registration;
      }
    }

    // A commit can briefly have no active target (old cleanup before replacement setup). Keep
    // the content connected to its current host whenever that host is still registered.
    if (!selected && this.currentTarget) {
      const current = this.targets.get(this.currentTarget);
      if (current) {
        selectedToken = this.currentTarget;
        selected = current;
      }
    }

    // If the current host was removed, move to a remaining fallback rather than detaching the
    // media root. The next active registration in the same commit will move it once more.
    if (!selected) {
      for (const [token, registration] of this.targets) {
        if (!selected || registration.registeredOrder > selected.registeredOrder) {
          selectedToken = token;
          selected = registration;
        }
      }
    }

    if (!selected || !selectedToken) return;
    moveStableRoot(this.root, selected.node);
    this.currentTarget = selectedToken;
  }
}

const VideoPresentationContext = createContext<VideoPresentationCoordinator | null>(null);

export interface VideoPresentationProps {
  /** Stable media subtree that moves between targets without remounting. */
  content: ReactNode;
  /** One or more targets; normally exactly one has `active=true`. */
  children: ReactNode;
}

/**
 * Keeps one media React/DOM subtree mounted while its presentation host changes. This is the
 * preferred inline↔MediaOverlay path for preserving an HTMLVideoElement decoder and local
 * ZoomPan state. The component itself must remain mounted across the transition.
 */
export function VideoPresentation({ content, children }: VideoPresentationProps) {
  const coordinatorRef = useRef<VideoPresentationCoordinator | null>(null);
  const [coordinator, setCoordinator] = useState<VideoPresentationCoordinator | null>(null);

  useBrowserLayoutEffect(() => {
    if (!coordinatorRef.current) {
      coordinatorRef.current = new VideoPresentationCoordinator(document);
    }
    setCoordinator((current) => current ?? coordinatorRef.current);
  }, []);

  if (!coordinator) {
    // Portals need a Document-owned root. SSR and the first client render stay empty; the layout
    // effect mounts the subtree before paint, so hydration never renders targets without context.
    return null;
  }

  return (
    <VideoPresentationContext.Provider value={coordinator}>
      {createPortal(content, coordinator.root, "c6-video-presentation")}
      {children}
    </VideoPresentationContext.Provider>
  );
}

export interface VideoPresentationTargetProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Whether this host currently owns the stable media subtree. */
  active: boolean;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

/** Empty layout host used by {@link VideoPresentation}. */
export const VideoPresentationTarget = forwardRef<HTMLDivElement, VideoPresentationTargetProps>(
  ({ active, style, ...rest }, forwardedRef) => {
    const coordinator = useContext(VideoPresentationContext);
    if (!coordinator) {
      throw new Error("VideoPresentationTarget must be rendered inside VideoPresentation.");
    }

    const tokenRef = useRef(Symbol("c6-video-presentation-target"));
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const activeRef = useRef(active);
    activeRef.current = active;

    const setTargetRef = useCallback(
      (node: HTMLDivElement | null) => {
        const previous = nodeRef.current;
        if (previous === node) return;

        if (previous) coordinator.removeTarget(tokenRef.current, previous);
        nodeRef.current = node;
        if (node) coordinator.updateTarget(tokenRef.current, node, activeRef.current);
        assignRef(forwardedRef, node);
      },
      [coordinator, forwardedRef],
    );

    useLayoutEffect(() => {
      const node = nodeRef.current;
      if (node) coordinator.updateTarget(tokenRef.current, node, active);
    }, [active, coordinator]);

    return (
      <div
        {...rest}
        ref={setTargetRef}
        data-c6-video-presentation-target=""
        data-active={active ? "true" : "false"}
        style={{ width: "100%", minWidth: 0, ...style }}
      />
    );
  },
);

VideoPresentationTarget.displayName = "VideoPresentationTarget";
